import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiSingleResponse } from '../../interfaces/responses/api-response';
import { ExternalNavigationService } from '../utils/external-navigation.service';

/**
 * D'où vient le clic qui ouvre la séance : l'agenda de l'application, ou le
 * bouton du mail de rappel. Journalisé avec la présence — c'est ce qui dira,
 * à la lecture, quel canal a réellement amené la personne en séance.
 */
export type JoinSource = 'App' | 'EmailReminder';

/**
 * Ce que reçoit l'appelant quand il n'y a rien d'ouvrable : la forme exacte du
 * refus `SESSION_NO_MEETING` du serveur, code et message. Un lien absent et un
 * lien qui n'est pas `http(s)` — saisi avant que le serveur ne le contrôle —
 * reviennent au même pour l'élève : la séance n'a pas de lien de visio. Les
 * écrans qui traduisent le code le traduisent ; ceux qui montrent le message
 * du serveur le montrent.
 */
export const NO_MEETING_ERROR = {
  error: { code: 'SESSION_NO_MEETING', message: "Cette séance n'a pas de réunion en ligne" },
} as const;

export interface SessionJoin {
  meetingUrl: string;
  /**
   * Le clic tombe-t-il dans la fenêtre où il vaut présence (30 min avant le
   * début à 30 min après la fin) ? Plus tôt, il est journalisé sans compter.
   */
  counted: boolean;
  /** Début de cette fenêtre, en heure de l'école (`2026-10-06T08:30:00`). */
  countsFrom: string;
}

export interface SessionParticipant {
  role: 'Professor' | 'Student';
  studentId?: number | null;
  professorId?: number | null;
  fullName?: string | null;
  firstJoinedAt: string;
  /** Canal du premier accès. */
  firstJoinSource?: JoinSource;
  minutesFromStart: number;
  joinCount: number;
}

/** Verdict de tenue de séance, calculé côté serveur depuis le journal d'accès. */
export type AttendanceVerdict =
  | 'Held'
  | 'Disputed'
  | 'NotHeld'
  | 'Pending'
  | 'NotApplicable'
  | 'Cancelled';

export interface SessionAttendance {
  appointmentId: number;
  title: string;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  subjectName?: string | null;
  groupName?: string | null;
  consultantName?: string | null;
  isOnline: boolean;
  professorJoined: boolean;
  professorJoinedAt?: string | null;
  expectedStudents: number;
  presentStudents: number;
  attendanceRate?: number | null;
  verdict: AttendanceVerdict;
  participants: SessionParticipant[];
}

/**
 * Accès aux réunions en ligne.
 *
 * Le lien Meet/Zoom n'est jamais ouvert directement : il transite par l'API,
 * qui journalise l'accès. C'est ce journal qui atteste après coup qu'une séance
 * s'est tenue et combien d'élèves l'ont suivie.
 */
@Injectable({ providedIn: 'root' })
export class SessionJoinService {
  private readonly http = inject(HttpClient);
  private readonly navigation = inject(ExternalNavigationService);
  private readonly base = `${environment.BASE_URL}/sessions`;

  /**
   * Journalise l'accès, avec sa source, et renvoie l'URL de réunion.
   *
   * Sans source, le clic vient de l'application — c'est le cas de tous les
   * appelants de l'agenda ; la page d'atterrissage du mail passe `EmailReminder`.
   */
  join(
    appointmentId: number,
    source: JoinSource = 'App',
  ): Observable<ApiSingleResponse<SessionJoin>> {
    return this.http.post<ApiSingleResponse<SessionJoin>>(
      `${this.base}/${appointmentId}/join`,
      { source },
    );
  }

  /** Bilan de présence d'une séance animée par le prof connecté. */
  myAttendance(
    appointmentId: number,
  ): Observable<ApiSingleResponse<SessionAttendance>> {
    return this.http.get<ApiSingleResponse<SessionAttendance>>(
      `${this.base}/${appointmentId}/attendance`,
    );
  }

  /**
   * Ouvre la réunion et journalise l'accès.
   *
   * Quand l'écran connaît déjà l'URL (`knownUrl`, lue sur la séance), la
   * réunion s'ouvre pendant le clic et l'accès se journalise à côté. Attendre
   * la réponse laissait l'onglet vide le temps du renouvellement du jeton et de
   * l'aller-retour : l'élève restait devant une page blanche, ou la voyait se
   * refermer sans un mot si l'un des deux échouait.
   *
   * Sans URL connue — l'inspecteur, qui n'est convié à rien — elle vient de
   * l'API. L'onglet est alors ouvert de façon synchrone, avant l'appel réseau :
   * ouvert après la réponse, il serait bloqué comme fenêtre surgissante puisqu'il
   * ne serait plus rattaché au clic de l'utilisateur.
   *
   * Une URL qui n'est pas `http(s)` n'est ouverte dans aucun des deux cas :
   * l'onglet réservé se referme et l'appelant reçoit `NO_MEETING_ERROR`.
   */
  openMeeting(
    appointmentId: number,
    onError?: (err: unknown) => void,
    knownUrl?: string | null,
  ): void {
    if (this.navigation.open(knownUrl)) {
      this.join(appointmentId).subscribe({ error: (err) => onError?.(err) });
      return;
    }

    const tab = this.navigation.reserveTab();

    this.join(appointmentId).subscribe({
      next: (res) => {
        if (!tab.navigate(res?.data?.meetingUrl)) onError?.(NO_MEETING_ERROR);
      },
      error: (err) => {
        tab.close();
        onError?.(err);
      },
    });
  }
}
